import type { AttachmentLink, DocumentBlock, DocumentKind, DocumentLinkTargetKind, DocumentVisibility, ExecutionTask, PlanDocument, Project, ProjectRecord } from "@/engine/types";
import { addComment, deleteComment, makeExecutionTask, updateComment } from "@/store/execution-task-ops";
import { makeRecord, recordCode } from "@/store/record-ops";
import { newId } from "@/store/tree-ops";
import { appendMention, detachMissingImages, plainTextFromBlocks, releaseCheckItem, replaceMentionWithText, stripCommentMarks } from "./doc-blocks";
import { addExplicitLink, linkCountSummary, markTargetsMissing, presentLink, removeLink, syncMentionLinks, targetSnapshot, type LinkCatalog } from "./doc-links";
import { DOCUMENT_KIND_LABEL, activity, isBlankDocument, keepDocumentBodies, makeDocument, nextDocumentSeq, selectionTitle, withActivity } from "./doc-model";

function kindChangeNote(kindName: string): string {
  const code = kindName.charCodeAt(kindName.length - 1);
  const jong = code >= 0xac00 && code <= 0xd7a3 ? (code - 0xac00) % 28 : -1;
  const particle = jong === 0 || jong === 8 ? "로" : "으로";
  return `종류를 ${kindName}${particle} 바꿨습니다`;
}

export function catalogOf(project: Pick<Project, "executionTasks" | "tasks" | "records">, wbs: ReadonlyMap<string, string> = new Map()): LinkCatalog {
  return {
    executionTasks: project.executionTasks,
    tasks: project.tasks.map((task) => ({ id: task.id, name: task.name, wbs: wbs.get(task.id), percentComplete: task.percentComplete })),
    records: project.records,
  };
}

export function createDocument(project: Project, input: { kind?: DocumentKind; authorId: string; now?: string; id?: string }): { project: Project; document: PlanDocument } {
  const now = input.now ?? new Date().toISOString();
  const document = makeDocument({ authorId: input.authorId, seq: nextDocumentSeq(project.documents), kind: input.kind ?? "memo", ...(input.id ? { id: input.id } : {}) }, now);
  return { project: { ...project, documents: [...project.documents, document], updatedAt: now }, document };
}

export function discardIfBlank(project: Project, id: string): { project: Project; discarded: boolean } {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc || doc.deletedAt || !isBlankDocument(doc)) return { project, discarded: false };
  return {
    project: {
      ...project,
      documents: project.documents.filter((item) => item.id !== id),
      documentLinks: project.documentLinks.filter((link) => link.documentId !== id),
    },
    discarded: true,
  };
}

export function patchDocument(
  project: Project,
  id: string,
  patch: Partial<Pick<PlanDocument, "title" | "kind" | "heldAt">>,
  actor: string,
  now = new Date().toISOString(),
): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc || doc.deletedAt) return null;
  let next = { ...doc };
  const notes: string[] = [];
  if (patch.title !== undefined && patch.title !== doc.title) {
    next = { ...next, title: patch.title };
    notes.push("제목을 바꿨습니다");
  }
  if (patch.kind && patch.kind !== doc.kind) {
    next = { ...next, kind: patch.kind };
    if (patch.kind !== "meeting") delete next.heldAt;
    notes.push(kindChangeNote(DOCUMENT_KIND_LABEL[patch.kind]));
  }
  if (patch.heldAt !== undefined && next.kind === "meeting" && patch.heldAt !== doc.heldAt) {
    if (patch.heldAt) next = { ...next, heldAt: patch.heldAt };
    else {
      delete next.heldAt;
    }
  }
  if (!notes.length && patch.heldAt === undefined) return project;
  for (const message of notes) next = withActivity(next, message, actor, now);
  next = { ...next, updatedAt: now };
  return replaceDoc(project, next, now);
}

export function setDocumentVisibility(project: Project, id: string, visibility: DocumentVisibility, actor: string, authorId: string, now = new Date().toISOString()): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc || doc.deletedAt || doc.visibility === visibility) return null;
  if (visibility === "private" && doc.authorId !== authorId) return null;
  const message = visibility === "plan" ? "공개로 바꿨습니다" : "비공개로 바꿨습니다";
  return replaceDoc(project, withActivity({ ...doc, visibility }, message, actor, now), now);
}

export function saveDocumentBody(project: Project, id: string, blocks: DocumentBlock[], actor: string, catalog: LinkCatalog, now = new Date().toISOString()): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc || doc.deletedAt) return null;
  const ids = new Set(doc.attachments.map((item) => item.id));
  const cleaned = stripCommentMarks(detachMissingImages(blocks, ids));
  const plainText = plainTextFromBlocks(cleaned);
  const documentLinks = syncMentionLinks(project.documentLinks, id, cleaned, catalog, actor, now);
  const next = { ...doc, blocks: cleaned, plainText, updatedAt: now };
  return { ...project, documents: project.documents.map((item) => (item.id === id ? next : item)), documentLinks, updatedAt: now };
}

export function trashDocument(project: Project, id: string, actor: string, now = new Date().toISOString()): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc || doc.deletedAt) return null;
  return replaceDoc(project, withActivity({ ...doc, deletedAt: now }, "휴지통으로 옮겼습니다", actor, now), now);
}

export function restoreDocument(project: Project, id: string, actor: string, now = new Date().toISOString()): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc?.deletedAt) return null;
  const next = { ...doc, updatedAt: now };
  delete next.deletedAt;
  return replaceDoc(project, withActivity(next, "복원했습니다", actor, now), now);
}

export function purgeDocument(project: Project, id: string): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc?.deletedAt) return null;
  return {
    ...project,
    documents: project.documents.filter((item) => item.id !== id),
    documentLinks: project.documentLinks.filter((link) => link.documentId !== id),
    updatedAt: new Date().toISOString(),
  };
}

export function linkDocument(
  project: Project,
  id: string,
  targetKind: DocumentLinkTargetKind,
  targetId: string,
  actor: string,
  catalog: LinkCatalog,
  now = new Date().toISOString(),
): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc || doc.deletedAt) return null;
  const snapshot = targetSnapshot(catalog, targetKind, targetId);
  if (!snapshot) return null;
  const added = addExplicitLink(project.documentLinks, { documentId: id, targetKind, targetId, snapshot, actor, now });
  if (added.links === project.documentLinks) return project;
  const label = [snapshot.code, snapshot.title].filter(Boolean).join(" ");
  const message = targetKind === "record" ? `${snapshot.code ?? "기록"} 연결을 추가했습니다` : `작업 「${snapshot.title}」을 연결했습니다`;
  return {
    ...replaceDoc(project, withActivity(doc, message, actor, now), now)!,
    documentLinks: added.links,
  };
}

export function unlinkDocument(project: Project, id: string, linkId: string, actor: string, catalog: LinkCatalog, now = new Date().toISOString()): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  const link = project.documentLinks.find((item) => item.id === linkId && item.documentId === id);
  if (!doc || !link) return null;
  const chip = presentLink(link, catalog);
  const label = [chip.code, chip.title].filter(Boolean).join(" ");
  const blocks = replaceMentionWithText(doc.blocks, link.targetKind, link.targetId, label || chip.title);
  const message = link.targetKind === "record" ? `${chip.code ?? "기록"} 연결을 해제했습니다` : `작업 「${chip.title}」 연결을 해제했습니다`;
  const next = withActivity({ ...doc, blocks, plainText: plainTextFromBlocks(blocks) }, message, actor, now);
  return { ...replaceDoc(project, next, now)!, documentLinks: removeLink(project.documentLinks, linkId) };
}

export function setDocumentAttachments(project: Project, id: string, attachments: AttachmentLink[], actor: string, now = new Date().toISOString()): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc || doc.deletedAt) return null;
  const before = new Set(doc.attachments.map((item) => item.id));
  const after = new Set(attachments.map((item) => item.id));
  const added = [...after].filter((item) => !before.has(item)).length;
  const removed = [...before].filter((item) => !after.has(item)).length;
  let next = { ...doc, attachments, blocks: detachMissingImages(doc.blocks, after), updatedAt: now };
  next = { ...next, plainText: plainTextFromBlocks(next.blocks) };
  if (added) next = withActivity(next, "첨부를 추가했습니다", actor, now);
  if (removed) next = withActivity(next, "첨부를 뺐습니다", actor, now);
  if (!added && !removed) return replaceDoc(project, next, now);
  return replaceDoc(project, next, now);
}

export function commentOnDocument(project: Project, id: string, author: string, body: string, options?: { parentId?: string; attachments?: AttachmentLink[] }, now = new Date().toISOString()): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc || doc.deletedAt) return null;
  const commented = addComment(doc, author, body, options, now);
  if (commented === doc) return null;
  const message = options?.parentId ? "대댓글을 남겼습니다" : "댓글을 남겼습니다";
  return replaceDoc(project, withActivity(commented, message, author, now), now);
}

export function updateDocumentComment(project: Project, id: string, commentId: string, body: string, actor: string, options?: { attachments?: AttachmentLink[] }, now = new Date().toISOString()): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc) return null;
  return replaceDoc(project, withActivity(updateComment(doc, commentId, body, options, now), "댓글을 수정했습니다", actor, now), now);
}

export function deleteDocumentComment(project: Project, id: string, commentId: string, actor: string, now = new Date().toISOString(), options?: { allowOthers?: boolean }): Project | null {
  const doc = project.documents.find((item) => item.id === id);
  if (!doc) return null;
  const comment = doc.comments.find((item) => item.id === commentId);
  if (!comment) return null;
  if (comment.author !== actor && !options?.allowOthers) return null;
  return replaceDoc(project, withActivity(deleteComment(doc, commentId, now), "댓글을 삭제했습니다", actor, now), now);
}

/**
 * 제목·연결 실행 취소는 그 사이 저장된 본문을 덮지 않는다.
 * 스냅샷의 첨부에서 빠진 이미지만 안내 문단으로 바꾸고, 언급은 본문과 다시 맞춘다.
 */
export function restoreKeepingBodies(snapshot: Project, live: Project, catalog: LinkCatalog, actor: string, now = new Date().toISOString()): Project {
  const overlaid = keepDocumentBodies(snapshot, live);
  let documentLinks = overlaid.documentLinks;
  const documents = overlaid.documents.map((doc) => {
    const ids = new Set(doc.attachments.map((item) => item.id));
    const blocks = detachMissingImages(doc.blocks, ids);
    const changed = JSON.stringify(blocks) !== JSON.stringify(doc.blocks);
    const next = changed ? { ...doc, blocks, plainText: plainTextFromBlocks(blocks) } : doc;
    documentLinks = syncMentionLinks(documentLinks, next.id, next.blocks, catalog, actor, now);
    return next;
  });
  return { ...overlaid, documents, documentLinks };
}

export function createWorkFromDocument(
  project: Project,
  input: { documentId: string; blockId?: string; title: string; kind: "task" | "issue"; actor: string; now?: string; catalog: LinkCatalog },
): { project: Project; taskId?: string; recordId?: string } | null {
  const doc = project.documents.find((item) => item.id === input.documentId);
  const title = selectionTitle(input.title);
  if (!doc || doc.deletedAt || !title) return null;
  const now = input.now ?? new Date().toISOString();
  const released = input.blockId ? releaseCheckItem(doc.blocks, input.blockId) : null;
  const baseBlocks = released ?? doc.blocks;
  if (input.kind === "task") {
    const maxOrder = project.executionTasks.reduce((max, task) => Math.max(max, task.order), -1);
    let task: ExecutionTask = makeExecutionTask({ order: maxOrder + 1, title }, now);
    task = { ...task, activity: [...task.activity, activity(`${documentCodeOf(doc)}에서 생성`, input.actor, now)] };
    const blocks = appendMention(baseBlocks, input.blockId, "executionTask", task.id);
    const snapshot = { title };
    const linked = addExplicitLink(project.documentLinks, {
      documentId: doc.id,
      targetKind: "executionTask",
      targetId: task.id,
      snapshot,
      actor: input.actor,
      now,
      ...(input.blockId ? { blockId: input.blockId } : {}),
    });
    const withMention = syncMentionLinks(linked.links, doc.id, blocks, { ...input.catalog, executionTasks: [...input.catalog.executionTasks, task] }, input.actor, now);
    const nextDoc = withActivity({ ...doc, blocks, plainText: plainTextFromBlocks(blocks) }, `작업 「${title}」을 연결했습니다`, input.actor, now);
    return {
      project: { ...replaceDoc(project, nextDoc, now)!, executionTasks: [...project.executionTasks, task], documentLinks: withMention },
      taskId: task.id,
    };
  }
  const seq = nextRecordSeq(project.records, "issue");
  let record: ProjectRecord = makeRecord({ kind: "issue", seq, title, author: input.actor }, now);
  record = { ...record, activity: [...record.activity, activity(`${documentCodeOf(doc)}에서 생성`, input.actor, now)] };
  const blocks = appendMention(baseBlocks, input.blockId, "record", record.id);
  const snapshot = { code: recordCode(record), title };
  const linked = addExplicitLink(project.documentLinks, {
    documentId: doc.id,
    targetKind: "record",
    targetId: record.id,
    snapshot,
    actor: input.actor,
    now,
    ...(input.blockId ? { blockId: input.blockId } : {}),
  });
  const withMention = syncMentionLinks(linked.links, doc.id, blocks, { ...input.catalog, records: [...input.catalog.records, record] }, input.actor, now);
  const nextDoc = withActivity({ ...doc, blocks, plainText: plainTextFromBlocks(blocks) }, `${snapshot.code} 연결을 추가했습니다`, input.actor, now);
  return {
    project: { ...replaceDoc(project, nextDoc, now)!, records: [...project.records, record], documentLinks: withMention },
    recordId: record.id,
  };
}

export function tombstoneTargets(project: Project, kind: DocumentLinkTargetKind, ids: readonly string[], catalog: LinkCatalog, now = new Date().toISOString()): Project {
  return { ...project, documentLinks: markTargetsMissing(project.documentLinks, catalog, kind, ids, now) };
}

export function documentNoticeCount(project: Project, kind: DocumentLinkTargetKind, ids: readonly string[], canRead: (doc: PlanDocument) => boolean): number {
  const targets = new Set(ids);
  const docs = new Map(project.documents.map((doc) => [doc.id, doc]));
  const found = new Set<string>();
  for (const link of project.documentLinks) {
    if (link.targetKind !== kind || !targets.has(link.targetId)) continue;
    const doc = docs.get(link.documentId);
    if (!doc || doc.deletedAt || !canRead(doc)) continue;
    found.add(doc.id);
  }
  return found.size;
}

export function linkSummaryOf(project: Project, documentId: string) {
  return linkCountSummary(project.documentLinks.filter((link) => link.documentId === documentId));
}

function replaceDoc(project: Project, doc: PlanDocument, now: string): Project {
  return { ...project, documents: project.documents.map((item) => (item.id === doc.id ? doc : item)), updatedAt: now };
}

function documentCodeOf(doc: PlanDocument): string {
  return `DOC-${doc.seq}`;
}

function nextRecordSeq(records: readonly ProjectRecord[], kind: ProjectRecord["kind"]): number {
  let max = 0;
  for (const record of records) if (record.kind === kind && record.seq > max) max = record.seq;
  return max + 1;
}

export function newDocumentId(): string {
  return newId("doc");
}

/** 빈 문서를 만든 실행 취소 한 단계인지. 나가며 버릴 때 그 단계도 함께 걷어 낸다. */
export function blankCreateStep(before: Project, after: Project, id: string): boolean {
  const doc = after.documents.find((item) => item.id === id);
  if (!doc || !isBlankDocument(doc)) return false;
  if (before.documents.some((item) => item.id === id)) return false;
  if (after.documents.length !== before.documents.length + 1) return false;
  if (after.documentLinks.length !== before.documentLinks.length) return false;
  if (after.executionTasks.length !== before.executionTasks.length) return false;
  if (after.records.length !== before.records.length) return false;
  if (after.tasks.length !== before.tasks.length) return false;
  return true;
}
